import { useEffect, useRef, useState } from 'react';
import { chatApi } from '../api';
import './ChatPanel.css';

function timeLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function ChatPanel({ isAuthenticated, hasCreatorPlus, variant = 'panel' }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(true);
  const lastIdRef = useRef(0);
  const listRef = useRef(null);
  const sinceLastPostRef = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await chatApi.fetch(lastIdRef.current, 50);
        if (cancelled) return;
        if (data.length > 0) {
          setMessages(prev => {
            const map = new Map(prev.map(m => [m.id, m]));
            data.forEach(m => map.set(m.id, m));
            return Array.from(map.values()).sort((a, b) => a.id - b.id).slice(-50);
          });
          lastIdRef.current = data[data.length - 1].id;
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    load();
    const t = setInterval(load, 3000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const content = input.trim();
    if (!content || sending) return;
    if (content.length > 1000) return;
    const elapsed = Date.now() - sinceLastPostRef.current;
    if (elapsed < 1500) {
      setError('Bitte warte einen Moment vor der nächsten Nachricht.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const msg = await chatApi.send(content);
      setMessages(prev => [...prev, msg].slice(-50));
      lastIdRef.current = Math.max(lastIdRef.current, msg.id);
      setInput('');
      sinceLastPostRef.current = Date.now();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isAuthenticated) {
    if (variant === 'panel') return null;
    return (
      <div className="page">
        <h1>💬 Community-Chat</h1>
        <p className="muted">Du musst mit Discord angemeldet sein, um am Chat teilzunehmen.</p>
      </div>
    );
  }

  if (!hasCreatorPlus) {
    if (variant === 'panel') return null;
    return (
      <div className="page chat-page">
        <div className="chat-page-card">
          <div className="chat-header">
            <span className="chat-title">💬 Community-Chat</span>
          </div>
          <div className="chat-restricted-card">
            <div className="chat-restricted-inner">
              <h2>Zugang beschränkt</h2>
              <p>Nur Creator+ Mitglieder können den Chat nutzen. Bitte beachte die Hinweise auf deiner Streamer-Account-Seite.Wenn du auch Creator+ werden möchtest erstelle ein Ticket im Discord.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'panel') {
    return (
      <div className={`chat-panel ${open ? 'open' : 'closed'}`}>
        <button className="chat-toggle" onClick={() => setOpen(o => !o)} title={open ? 'Chat einklappen' : 'Chat öffnen'}>
          {open ? '›' : '‹'}
        </button>
        {open && (
          <div className="chat-inner">
            <div className="chat-header">
              <span className="chat-title">💬 Community-Chat</span>
              <span className="chat-count">{messages.length}</span>
            </div>
            {error && <div className="chat-error">{error}</div>}
            <div className="chat-list" ref={listRef}>
              {messages.length === 0 ? (
                <div className="chat-empty">Noch keine Nachrichten. Sei der Erste!</div>
              ) : (
                messages.map(m => (
                  <div key={m.id} className="chat-msg">
                    <img
                      src={m.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png'}
                      alt={m.display_name || m.username || 'User'}
                      className="chat-avatar"
                    />
                    <div className="chat-msg-body">
                      <div className="chat-msg-meta">
                        <span className="chat-name">{m.display_name || m.username || 'Unbekannt'}</span>
                        <span className="chat-time">{timeLabel(m.created_at)}</span>
                      </div>
                      <div className="chat-text">{m.content}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <form className="chat-input-row" onSubmit={handleSend}>
              <textarea
                className="chat-input"
                placeholder="Nachricht schreiben…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                maxLength={1000}
                rows={2}
                disabled={sending}
              />
              <button type="submit" className="chat-send" disabled={sending || !input.trim()}>
                ↑
              </button>
            </form>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page chat-page">
      <div className="chat-page-card">
        <div className="chat-header">
          <span className="chat-title">💬 Community-Chat</span>
          <span className="chat-count">{messages.length} Nachricht{messages.length === 1 ? '' : 'en'}</span>
        </div>
        {error && <div className="chat-error">{error}</div>}
        <div className="chat-list chat-list-page" ref={listRef}>
          {messages.length === 0 ? (
            <div className="chat-empty">Noch keine Nachrichten. Sei der Erste!</div>
          ) : (
            messages.map(m => (
              <div key={m.id} className="chat-msg">
                <img
                  src={m.avatar_url || 'https://cdn.discordapp.com/embed/avatars/0.png'}
                  alt={m.display_name || m.username || 'User'}
                  className="chat-avatar"
                />
                <div className="chat-msg-body">
                  <div className="chat-msg-meta">
                    <span className="chat-name">{m.display_name || m.username || 'Unbekannt'}</span>
                    <span className="chat-time">{timeLabel(m.created_at)}</span>
                  </div>
                  <div className="chat-text">{m.content}</div>
                </div>
              </div>
            ))
          )}
        </div>
        <form className="chat-input-row" onSubmit={handleSend}>
          <textarea
            className="chat-input"
            placeholder="Nachricht schreiben…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            maxLength={1000}
            rows={2}
            disabled={sending}
          />
          <button type="submit" className="chat-send" disabled={sending || !input.trim()}>
            ↑
          </button>
        </form>
      </div>
    </div>
  );
}
